import { LoadingOutlined } from '@ant-design/icons';
import { Box, Button, Flex } from '@chakra-ui/react';
import {
  type DialOutOption,
  AudioChangeAction,
  CaptureVideoOption,
  DialoutState,
  LiveStreamStatus,
  MobileVideoFacingMode,
  MutedSource,
  RecordingStatus,
  SharePrivilege,
  ShareStatus
} from '@zoom/videosdk';
import { message, Modal } from 'antd';
import classNames from 'classnames';
import { ElementRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IconFont } from '../../../component/icon-font';
import { LessonContext } from '../../../context/lesson-context';
import ZoomMediaContext from '../../../context/media-context';
import ZoomContext from '../../../context/zoom-context';
import { useMount, useUnmount } from '../../../hooks';
import useDeviceSettingContext from '../../../hooks/useDeviceSettingContext';
import useLanguageSelector from '../../../hooks/useLanguageSelector';
import useTimer from '../../../hooks/useTimer';
import type { Participant } from '../../../index-types';
import { detectDeviceType, isMobile } from '../../../utils/DeviceType';
import { pipWindow } from '../../../utils/pip-utils';
import { isAndroidOrIOSBrowser } from '../../../utils/platform';
import { setLocalStorage } from '../../../utils/web-storage-utils';
import { useParticipantsChange } from '../hooks/useParticipantsChange';
import { getPhoneCallStatusDescription } from '../video-constants';
import type { MediaDevice } from '../video-types';
import CameraButton from './camera';
import LeaveButton from './leave';
import MicrophoneButton from './microphone';
import LeaveModal from './Modal/LeaveModal';
import { type RecordButtonProps, getRecordingButtons } from './recording';
import { ScreenShareButton } from './screen-share';
import ForceExitTimer from './Timer/FoceExitTimer';
import RemainingTimeTimer from './Timer/RemainingTimeTimer';
import './video-footer.scss';
import { VideoMaskModel } from './video-mask-modal';
// import { useSegmentedZoomRecorder } from '../../../hooks/useVideoRecorder';
import { type VideoPlayer } from '@zoom/videosdk';
import useLogger from '../../../hooks/useLogger';
import AudioRecording from './audio-recording';
import VideoRecording from './video-recording';
import { useLocation } from 'react-router-dom';
import { SendLoggerService } from '../../../service/send-logger-service';
interface VideoFooterProps {
  className?: string;
  selfShareCanvas?: HTMLCanvasElement | HTMLVideoElement | null;
  sharing?: boolean;
  participants?: Participant[];
  togleChatView: () => void;
  startPiPView: () => void;
}
type ModalType = '' | 'Leave';
const isAudioEnable = typeof AudioWorklet === 'function';
const VideoFooter = (props: VideoFooterProps) => {
  const currentPage = useLocation().pathname;
  const LANG = useLanguageSelector();
  const deviceSettingCtx = useDeviceSettingContext();
  const { className, selfShareCanvas, sharing, togleChatView, startPiPView, participants } = props;
  const zmClient = useContext(ZoomContext);
  const { mediaStream } = useContext(ZoomMediaContext);
  const liveTranscriptionClient = zmClient.getLiveTranscriptionClient();
  const liveStreamClient = zmClient.getLiveStreamClient();
  const recordingClient = zmClient.getRecordingClient();
  const [isStartedAudio, setIsStartedAudio] = useState(
    zmClient.getCurrentUserInfo() && zmClient.getCurrentUserInfo().audio !== ''
  );
  const [isStartedVideo, setIsStartedVideo] = useState(zmClient.getCurrentUserInfo()?.bVideoOn);
  const [audio, setAudio] = useState(zmClient.getCurrentUserInfo()?.audio);
  const [isSupportPhone, setIsSupportPhone] = useState(false);
  const [phoneCountryList, setPhoneCountryList] = useState<any[]>([]);
  const [phoneCallStatus, setPhoneCallStatus] = useState<DialoutState>();
  const [isStartedLiveTranscription, setIsStartedLiveTranscription] = useState(false);
  const [isDisableCaptions, setIsDisableCaptions] = useState(false);
  const [isMirrored, setIsMirrored] = useState(false);
  // const [isBlur, setIsBlur] = useState(mediaStream?.getVirtualbackgroundStatus().imageSrc === 'blur');
  const [isBlur, setIsBlur] = useState(deviceSettingCtx.virtualBackground === 'blur');
  const [isMuted, setIsMuted] = useState(!!zmClient.getCurrentUserInfo()?.muted);
  // const [activeMicrophone, setActiveMicrophone] = useState(mediaStream?.getActiveMicrophone());
  const [activeMicrophone, setActiveMicrophone] = useState<string | undefined>(deviceSettingCtx.activeMicrophone);
  // const [activeSpeaker, setActiveSpeaker] = useState(mediaStream?.getActiveSpeaker());
  const [activeSpeaker, setActiveSpeaker] = useState<string | undefined>(deviceSettingCtx.activeSpeaker);
  // const [activeCamera, setActiveCamera] = useState(mediaStream?.getActiveCamera());
  const [activeCamera, setActiveCamera] = useState<string | undefined>(deviceSettingCtx.activeCamera);
  const [micList, setMicList] = useState<MediaDevice[]>(mediaStream?.getMicList() ?? []);
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>(mediaStream?.getSpeakerList() ?? []);
  const [cameraList, setCameraList] = useState<MediaDevice[]>(mediaStream?.getCameraList() ?? []);
  const [isComputerAudioDisabled, setIsComputerAudioDisabled] = useState(false);
  const [sharePrivilege, setSharePrivileg] = useState(SharePrivilege.MultipleShare);
  const [caption, setCaption] = useState({ text: '', isOver: false });
  const [activePlaybackUrl, setActivePlaybackUrl] = useState('');
  const [isMicrophoneForbidden, setIsMicrophoneForbidden] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState<'' | RecordingStatus>(
    recordingClient?.getCloudRecordingStatus() || ''
  );
  const [recordingIsoStatus, setRecordingIsoStatus] = useState<'' | RecordingStatus>('');
  const [liveStreamVisible, setLiveStreamVisible] = useState(false);
  const [liveStreamStatus, setLiveStreamStatus] = useState(liveStreamClient?.getLiveStreamStatus());
  // Video Mask
  const [videoMaskVisible, setVideoMaskVisible] = useState(false);

  const shareStatus = mediaStream?.getShareStatus();
  const [openModal, setOpenModal] = useState<ModalType>('');
  const [lessonClosing, setLessonClosing] = useState(false);
  const [audioTermination, setAudioTermination] = useState(false);
  const [videoTermination, setVideoTermination] = useState<
    {
      userId: number;
      terminated: boolean;
    }[]
  >([]);
  const [completeTermination, setCompleteTermination] = useState(false);
  const [recordingVideoUsers, setRecordingVideoUsers] = useState<number[]>([]);
  const sendLoggerService = useMemo(() => new SendLoggerService(), []);

  useEffect(() => {
    if (!lessonClosing) {
      return;
    }
    if (!audioTermination) {
      return;
    }
    if (videoTermination.some((e) => !e.terminated)) {
      return;
    }
    zmClient.leave().then((e) => {
      setCompleteTermination(true);
      window.close();
    });
  }, [lessonClosing, audioTermination, videoTermination]);

  const onAudioTermination = (value: boolean) => {
    setAudioTermination(value);
  };
  const onVideoTermination = (value: boolean, userId: number) => {
    const state = [
      {
        userId: zmClient.getSessionInfo().userId,
        terminated: false
      },
      ...(participants || [])
    ].map((participant) => {
      return {
        userId: participant.userId,
        terminated: false
      };
    });

    setVideoTermination((prev) => {
      const arr = [...prev, ...state, { userId: userId, terminated: value }].reduce((acc, { userId, terminated }) => {
        if (!acc[userId] || value === true) {
          acc[userId] = { userId, terminated: value };
        }
        return acc;
      }, {} as any);

      return Object.values(arr);
    });
  };
  const lessonCtx = useContext(LessonContext);

  const { timerState, timerDuration, remainingSecondsToClose } = useTimer(lessonCtx.endDatetime);

  useParticipantsChange(zmClient, () => {
    const currentUser = zmClient.getCurrentUserInfo();
    setIsMuted(!!currentUser?.muted);
    setIsStartedVideo(!!currentUser?.bVideoOn);
  });

  const checkPermission = () => {
    try {
      const detectedDevice = detectDeviceType();

      if (detectedDevice.os === 'ios') {
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const cam = devices.find((device) => device.kind === 'videoinput');
          if (cam) {
            navigator.mediaDevices
              .getUserMedia({ video: true })
              .then((stream) => {
                stream.getTracks().forEach((track) => track.stop());
                sendLoggerService.sendLog({
                  action: '権限確認(iOS)',
                  lessonId: lessonCtx?.lessonId ?? '',
                  roomId: lessonCtx?.roomId ?? '',
                  userId: `${zmClient.getSessionInfo().userId}`,
                  page: currentPage,
                  advancedInfo: {
                    cam,
                    tracks: stream.getTracks()
                  }
                });
              })
              .catch((error) => {
                console.log('No camera device found ', error);
                sendLoggerService.sendLog({
                  action: '権限確認(iOS)',
                  lessonId: lessonCtx?.lessonId ?? '',
                  roomId: lessonCtx?.roomId ?? '',
                  userId: `${zmClient.getSessionInfo().userId}`,
                  page: currentPage,
                  advancedInfo: {
                    cam,
                    error
                  }
                });
              });
          } else {
            console.log('No camera device found');
            sendLoggerService.sendLog({
              action: '権限確認(iOS)',
              lessonId: lessonCtx?.lessonId ?? '',
              roomId: lessonCtx?.roomId ?? '',
              userId: `${zmClient.getSessionInfo().userId}`,
              page: currentPage,
              advancedInfo: {
                cam
              }
            });
          }
        });
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            const audioOutputs = devices.filter((device) => device.kind === 'audiooutput');

            if (audioOutputs.length > 0) {
            } else {
            }

            sendLoggerService.sendLog({
              action: '権限確認(iOS)',
              lessonId: lessonCtx?.lessonId ?? '',
              roomId: lessonCtx?.roomId ?? '',
              userId: `${zmClient.getSessionInfo().userId}`,
              page: currentPage,
              advancedInfo: {
                audioOutputs
              }
            });
          })
          .catch((error) => {
            console.error('Error checking audio output devices:', error);
            sendLoggerService.sendLog({
              action: '権限確認(iOS)',
              lessonId: lessonCtx?.lessonId ?? '',
              roomId: lessonCtx?.roomId ?? '',
              userId: `${zmClient.getSessionInfo().userId}`,
              page: currentPage,
              advancedInfo: {
                error
              }
            });
          });
      }
    } catch (error) {
      sendLoggerService.sendLog({
        action: 'エラー',
        lessonId: lessonCtx?.lessonId ?? '',
        roomId: lessonCtx?.roomId ?? '',
        userId: `${zmClient.getSessionInfo().userId}`,
        page: currentPage,
        advancedInfo: {
          error
        }
      });
    }
  };

  const onCameraClick = useCallback(async () => {
    checkPermission();
    if (isStartedVideo) {
      sendLoggerService.sendLog({
        action: 'カメラボタン押下(ON->OFF)',
        lessonId: lessonCtx?.lessonId ?? '',
        roomId: lessonCtx?.roomId ?? '',
        userId: `${zmClient.getSessionInfo().userId}`,
        page: currentPage,
        advancedInfo: {}
      });

      try {
        await mediaStream?.stopVideo();
      } catch (error) {
        logger.error('stop error', error);
        sendLoggerService.sendLog({
          action: 'カメラ停止エラー',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {
            error: error
          }
        });
      }

      if (activePlaybackUrl) {
        await mediaStream?.switchMicrophone('default');
        setActiveMicrophone(mediaStream?.getActiveMicrophone());
        setActivePlaybackUrl('');
      }
    } else {
      sendLoggerService.sendLog({
        action: 'カメラボタン押下(OFF->ON)',
        lessonId: lessonCtx?.lessonId ?? '',
        roomId: lessonCtx?.roomId ?? '',
        userId: `${zmClient.getSessionInfo().userId}`,
        page: currentPage,
        advancedInfo: {}
      });

      const startVideoOptions: CaptureVideoOption = {
        hd: true,
        fullHd: true,
        ptz: mediaStream?.isBrowserSupportPTZ(),
        originalRatio: true
      };

      if (mediaStream?.isSupportVirtualBackground() && isBlur) {
        Object.assign(startVideoOptions, { virtualBackground: { imageUrl: 'blur' } });
      }
      if (!isMobile()) {
        Object.assign(startVideoOptions, { cameraId: deviceSettingCtx.activeCamera });
      }
      try {
        // SharedArrayBuffer(SAB)を使えない場合、描画不可。 startVideoの実行後にatatchVideoも使用してVideoタグで再生する必要あり。
        // SharedArrayBufferはCOOP、COEPをレスポンスヘッダに含めることで使えるようになる。（CloudFrontで設定済み）
        // https://developers.zoom.us/docs/video-sdk/web/sharedarraybuffer/
        // https://repost.aws/questions/QUVRdl8CFMSF6lDMtj9Lr0gQ/coep-and-coop-response-headers-with-s3-cloudfront
        await mediaStream?.startVideo(startVideoOptions);
      } catch (error) {
        logger.error(error);

        sendLoggerService.sendLog({
          action: 'カメラ開始エラー',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {
            error: error
          }
        });
      }
    }
  }, [mediaStream, isStartedVideo, isBlur, activePlaybackUrl]);
  const onMicrophoneClick = useCallback(async () => {
    if (isStartedAudio) {
      if (isMuted) {
        sendLoggerService.sendLog({
          action: 'マイクボタン押下(Mute->Unmute)',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {}
        });

        await mediaStream?.unmuteAudio();
      } else {
        sendLoggerService.sendLog({
          action: 'マイクボタン押下(Unmute->Mute)',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {}
        });

        await mediaStream?.muteAudio();
      }
    } else {
      try {
        sendLoggerService.sendLog({
          action: 'マイクボタン押下(AudioON)',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {}
        });

        if (activePlaybackUrl) {
          await mediaStream?.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
        } else {
          await mediaStream?.startAudio({ highBitrate: true });
        }
        setActiveMicrophone(mediaStream?.getActiveMicrophone());
      } catch (e: any) {
        if (e.type === 'INSUFFICIENT_PRIVILEGES' && e.reason === 'USER_FORBIDDEN_MICROPHONE') {
          setIsMicrophoneForbidden(true);
        }
        console.warn(e);
      }
      setIsStartedAudio(true);
    }
  }, [mediaStream, isStartedAudio, isMuted, activePlaybackUrl]);
  const onMicrophoneMenuClick = async (key: string) => {
    if (mediaStream) {
      const [type, deviceId] = key.split('|');
      if (type === 'microphone') {
        if (deviceId !== activeMicrophone) {
          sendLoggerService.sendLog({
            action: 'マイク切替ボタン押下',
            lessonId: lessonCtx?.lessonId ?? '',
            roomId: lessonCtx?.roomId ?? '',
            userId: `${zmClient.getSessionInfo().userId}`,
            page: currentPage,
            advancedInfo: {
              device: micList.find((e) => e.deviceId === deviceId)
            }
          });

          await mediaStream.switchMicrophone(deviceId).catch((err) => {
            sendLoggerService.sendLog({
              action: 'マイク切替エラー',
              lessonId: lessonCtx?.lessonId ?? '',
              roomId: lessonCtx?.roomId ?? '',
              userId: `${zmClient.getSessionInfo().userId}`,
              page: currentPage,
              advancedInfo: {
                error: err
              }
            });
          });
          setActiveMicrophone(mediaStream.getActiveMicrophone());
        }
      } else if (type === 'speaker') {
        if (deviceId !== activeSpeaker) {
          sendLoggerService.sendLog({
            action: 'スピーカー切替ボタン押下',
            lessonId: lessonCtx?.lessonId ?? '',
            roomId: lessonCtx?.roomId ?? '',
            userId: `${zmClient.getSessionInfo().userId}`,
            page: currentPage,
            advancedInfo: {
              device: speakerList.find((e) => e.deviceId === deviceId)
            }
          });

          await mediaStream.switchSpeaker(deviceId).catch((err) => {
            sendLoggerService.sendLog({
              action: 'スピーカー切替エラー',
              lessonId: lessonCtx?.lessonId ?? '',
              roomId: lessonCtx?.roomId ?? '',
              userId: `${zmClient.getSessionInfo().userId}`,
              page: currentPage,
              advancedInfo: {
                error: err
              }
            });
          });
          setActiveSpeaker(mediaStream.getActiveSpeaker());
        }
      } else if (type === 'leave audio') {
        sendLoggerService.sendLog({
          action: 'マイクボタン押下(AudioOFF)',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {}
        });
        if (audio === 'computer') {
          await mediaStream.stopAudio();
          setIsMicrophoneForbidden(false);
        } else if (audio === 'phone') {
          await mediaStream.hangup();
          setPhoneCallStatus(undefined);
        }
      }
    }
  };
  const [cameraSwitching, setCameraSwitching] = useState<boolean>(false);
  const onSwitchCamera = async (key: string) => {
    sendLoggerService.sendLog({
      action: 'カメラ切替ボタン押下',
      lessonId: lessonCtx?.lessonId ?? '',
      roomId: lessonCtx?.roomId ?? '',
      userId: `${zmClient.getSessionInfo().userId}`,
      page: currentPage,
      advancedInfo: {
        device: cameraList.find((e) => e.deviceId === key),
        cameraSwitching
      }
    });
    if (cameraSwitching) {
      return;
    }
    if (mediaStream) {
      if (activeCamera !== key) {
        setCameraSwitching(true);
        await mediaStream.switchCamera(key).catch((err) => {
          sendLoggerService.sendLog({
            action: 'カメラ切替エラー',
            lessonId: lessonCtx?.lessonId ?? '',
            roomId: lessonCtx?.roomId ?? '',
            userId: `${zmClient.getSessionInfo().userId}`,
            page: currentPage,
            advancedInfo: {
              error: err
            }
          });
        });

        setActiveCamera(mediaStream.getActiveCamera());
        setCameraSwitching(false);
        if (activePlaybackUrl) {
          await mediaStream.switchMicrophone('default');
          setActiveMicrophone(mediaStream.getActiveMicrophone());
          setActivePlaybackUrl('');
        }
      }
    }
  };
  const onMirrorVideo = async () => {
    await mediaStream?.mirrorVideo(!isMirrored);
    setIsMirrored(!isMirrored);
  };
  const onBlurBackground = async () => {
    const isSupportVirtualBackground = mediaStream?.isSupportVirtualBackground();
    logger.log('bg', isSupportVirtualBackground);
    if (isSupportVirtualBackground) {
      if (isBlur) {
        setLocalStorage('VirtualBackground', 'none');
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, virtualBackground: 'none' };
        });
        await mediaStream?.updateVirtualBackgroundImage(undefined);
      } else {
        setLocalStorage('VirtualBackground', 'blur');
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, virtualBackground: 'blur' };
        });
        await mediaStream?.updateVirtualBackgroundImage('blur');
      }
    } else {
      setVideoMaskVisible(true);
    }

    setIsBlur(!isBlur);
  };
  const onPhoneCall = async (code: string, phoneNumber: string, name: string, option: DialOutOption) => {
    await mediaStream?.inviteByPhone(code, phoneNumber, name, option);
  };
  const onPhoneCallCancel = async (code: string, phoneNumber: string, option: { callMe: boolean }) => {
    if ([DialoutState.Calling, DialoutState.Ringing, DialoutState.Accepted].includes(phoneCallStatus as any)) {
      await mediaStream?.cancelInviteByPhone(code, phoneNumber, option);
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 3000);
      });
    }
    return Promise.resolve();
  };
  const onHostAudioMuted = useCallback(
    (payload: any) => {
      const { action, source, type } = payload;
      const currentUser = zmClient.getCurrentUserInfo();
      setIsStartedAudio(currentUser.audio === 'computer' || currentUser.audio === 'phone');
      setAudio(currentUser.audio);
      if (action === AudioChangeAction.Join) {
        setIsStartedAudio(true);
        setAudio(type);
      } else if (action === AudioChangeAction.Leave) {
        setIsStartedAudio(false);
      } else if (action === AudioChangeAction.Muted) {
        if (source === MutedSource.PassiveByMuteOne) {
          message.info('Host muted you');
        }
      } else if (action === AudioChangeAction.Unmuted) {
        if (source === 'passive') {
          message.info('Host unmuted you');
        }
      }
    },
    [zmClient]
  );
  const onScreenShareClick = useCallback(async () => {
    if (mediaStream?.getShareStatus() === ShareStatus.End && selfShareCanvas) {
      try {
        sendLoggerService.sendLog({
          action: '画面共有ボタン押下(開始)',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {}
        });

        await mediaStream?.startShareScreen(selfShareCanvas, { requestReadReceipt: true });
      } catch (error) {
        sendLoggerService.sendLog({
          action: '画面共有開始エラー',
          lessonId: lessonCtx?.lessonId ?? '',
          roomId: lessonCtx?.roomId ?? '',
          userId: `${zmClient.getSessionInfo().userId}`,
          page: currentPage,
          advancedInfo: {
            error: error
          }
        });

        console.error(error);
        pipWindow?.close();
      }
    } else {
      sendLoggerService.sendLog({
        action: '画面共有ボタン押下(終了)',
        lessonId: lessonCtx?.lessonId ?? '',
        roomId: lessonCtx?.roomId ?? '',
        userId: `${zmClient.getSessionInfo().userId}`,
        page: currentPage,
        advancedInfo: {}
      });
    }
  }, [mediaStream, selfShareCanvas]);

  const onLiveTranscriptionClick = useCallback(async () => {
    if (isDisableCaptions) {
      message.info('Captions has been disable by host.');
    } else if (isStartedLiveTranscription) {
      message.info('Live transcription has started.');
    } else if (!isStartedLiveTranscription) {
      await liveTranscriptionClient?.startLiveTranscription();
      setIsStartedLiveTranscription(true);
    }
  }, [isStartedLiveTranscription, isDisableCaptions, liveTranscriptionClient]);

  const onDisableCaptions = useCallback(
    async (disable: boolean) => {
      if (disable && !isDisableCaptions) {
        await liveTranscriptionClient?.disableCaptions(disable);
        setIsStartedLiveTranscription(false);
        setIsDisableCaptions(true);
      } else if (!disable && isDisableCaptions) {
        await liveTranscriptionClient?.disableCaptions(disable);
        setIsDisableCaptions(false);
      }
    },
    [isDisableCaptions, liveTranscriptionClient]
  );

  const onLeaveClick = useCallback(async () => {
    sendLoggerService.sendLog({
      action: '退室OKボタン押下',
      lessonId: lessonCtx?.lessonId ?? '',
      roomId: lessonCtx?.roomId ?? '',
      userId: `${zmClient.getSessionInfo().userId}`,
      page: currentPage,
      advancedInfo: {}
    });

    setLessonClosing(true);
  }, [zmClient]);

  const onEndClick = useCallback(async () => {
    await zmClient.leave(true);
  }, [zmClient]);

  const onPassivelyStopShare = useCallback(({ reason }: any) => {
    sendLoggerService.sendLog({
      action: '画面共有ボタン押下(終了)',
      lessonId: lessonCtx?.lessonId ?? '',
      roomId: lessonCtx?.roomId ?? '',
      userId: `${zmClient.getSessionInfo().userId}`,
      page: currentPage,
      advancedInfo: { reason }
    });

    console.log('passively stop reason:', reason);
    pipWindow?.close();
  }, []);
  const onDeviceChange = useCallback(() => {
    if (mediaStream) {
      setMicList(mediaStream.getMicList());
      setSpeakerList(mediaStream.getSpeakerList());
      if (!isAndroidOrIOSBrowser()) {
        setCameraList(mediaStream.getCameraList());
      }
      setActiveMicrophone(mediaStream.getActiveMicrophone());
      setActiveSpeaker(mediaStream.getActiveSpeaker());
      setActiveCamera(mediaStream.getActiveCamera());
    }
  }, [mediaStream]);

  const onRecordingChange = useCallback(() => {
    setRecordingStatus(recordingClient?.getCloudRecordingStatus() || '');
  }, [recordingClient]);

  const onRecordingISOChange = useCallback(
    (payload: any) => {
      if (payload?.userId === zmClient.getSessionInfo().userId || payload?.status === RecordingStatus.Ask) {
        setRecordingIsoStatus(payload?.status);
      }
      console.log('recording-iso-change', payload);
    },
    [zmClient]
  );

  const onDialOutChange = useCallback((payload: any) => {
    setPhoneCallStatus(payload.code);
  }, []);

  const onRecordingClick = async (key: string) => {
    switch (key) {
      case 'Record': {
        await recordingClient?.startCloudRecording();
        break;
      }
      case 'Resume': {
        await recordingClient?.resumeCloudRecording();
        break;
      }
      case 'Stop': {
        await recordingClient?.stopCloudRecording();
        break;
      }
      case 'Pause': {
        await recordingClient?.pauseCloudRecording();
        break;
      }
      case 'Status': {
        break;
      }
      default: {
        await recordingClient?.startCloudRecording();
      }
    }
  };
  const onShareAudioChange = useCallback(
    (payload: any) => {
      const { state } = payload;
      if (state === 'on') {
        if (!mediaStream?.isSupportMicrophoneAndShareAudioSimultaneously()) {
          setIsComputerAudioDisabled(true);
        }
      } else if (state === 'off') {
        setIsComputerAudioDisabled(false);
      }
    },
    [mediaStream]
  );
  const onHostAskToUnmute = useCallback(
    (payload: any) => {
      const { reason } = payload;
      Modal.confirm({
        title: 'Unmute audio',
        content: `Host ask to unmute audio, reason: ${reason}`,
        onOk: async () => {
          await mediaStream?.unmuteAudio();
        },
        onCancel() {
          console.log('cancel');
        }
      });
    },
    [mediaStream]
  );

  const onCaptionStatusChange = useCallback((payload: any) => {
    const { autoCaption } = payload;
    if (autoCaption) {
      message.info('Auto live transcription enabled!');
    }
  }, []);

  const onCaptionMessage = useCallback((payload: any) => {
    const { text, done } = payload;
    setCaption({
      text,
      isOver: done
    });
  }, []);

  const onCaptionDisable = useCallback((payload: any) => {
    setIsDisableCaptions(payload);
    if (payload) {
      setIsStartedLiveTranscription(false);
    }
  }, []);

  const onCanSeeMyScreen = useCallback(() => {
    message.info('Users can now see your screen', 1);
  }, []);
  const onSelectVideoPlayback = useCallback(
    async (url: string) => {
      if (activePlaybackUrl !== url) {
        await mediaStream?.switchCamera({ url, loop: true });
        if (isStartedAudio) {
          await mediaStream?.switchMicrophone({ url, loop: true });
        } else {
          await mediaStream?.startAudio({ mediaFile: { url, loop: true } });
        }
        setActivePlaybackUrl(url);
      }
    },
    [isStartedAudio, activePlaybackUrl, mediaStream]
  );

  const onLiveStreamClick = useCallback(() => {
    if (liveStreamStatus === LiveStreamStatus.Ended) {
      setLiveStreamVisible(true);
    } else if (liveStreamStatus === LiveStreamStatus.InProgress) {
      liveStreamClient?.stopLiveStream();
    }
  }, [liveStreamStatus, liveStreamClient]);
  const onLiveStreamStatusChange = useCallback((status: any) => {
    setLiveStreamStatus(status);
    if (status === LiveStreamStatus.Timeout) {
      message.error('Start live streaming timeout');
    }
  }, []);

  const onVideoScreenshotTaken = useCallback((payload: any) => {
    const { displayName, userId } = payload;
    message.info(`${displayName}(User:${userId}) just took a screenshot of your video`);
  }, []);
  const onShareViewScreenshotTaken = useCallback((payload: any) => {
    const { displayName, userId } = payload;
    message.info(`${displayName}(User:${userId}) just took a screenshot of your sharing`);
  }, []);

  useEffect(() => {
    zmClient.on('current-audio-change', onHostAudioMuted);
    zmClient.on('passively-stop-share', onPassivelyStopShare);
    zmClient.on('device-change', onDeviceChange);
    zmClient.on('recording-change', onRecordingChange);
    zmClient.on('individual-recording-change', onRecordingISOChange);
    zmClient.on('dialout-state-change', onDialOutChange);
    zmClient.on('share-audio-change', onShareAudioChange);
    zmClient.on('host-ask-unmute-audio', onHostAskToUnmute);
    zmClient.on('caption-status', onCaptionStatusChange);
    zmClient.on('caption-message', onCaptionMessage);
    zmClient.on('caption-host-disable', onCaptionDisable);
    zmClient.on('share-can-see-screen', onCanSeeMyScreen);
    zmClient.on('live-stream-status', onLiveStreamStatusChange);
    zmClient.on('video-screenshot-taken', onVideoScreenshotTaken);
    zmClient.on('share-content-screenshot-taken', onShareViewScreenshotTaken);
    return () => {
      zmClient.off('current-audio-change', onHostAudioMuted);
      zmClient.off('passively-stop-share', onPassivelyStopShare);
      zmClient.off('device-change', onDeviceChange);
      zmClient.off('recording-change', onRecordingChange);
      zmClient.off('individual-recording-change', onRecordingISOChange);
      zmClient.off('dialout-state-change', onDialOutChange);
      zmClient.off('share-audio-change', onShareAudioChange);
      zmClient.off('host-ask-unmute-audio', onHostAskToUnmute);
      zmClient.off('caption-status', onCaptionStatusChange);
      zmClient.off('caption-message', onCaptionMessage);
      zmClient.off('caption-host-disable', onCaptionDisable);
      zmClient.off('share-can-see-screen', onCanSeeMyScreen);
      zmClient.off('live-stream-status', onLiveStreamStatusChange);
      zmClient.off('video-screenshot-taken', onVideoScreenshotTaken);
      zmClient.off('share-content-screenshot-taken', onShareViewScreenshotTaken);
    };
  }, [
    zmClient,
    onHostAudioMuted,
    onPassivelyStopShare,
    onDeviceChange,
    onRecordingChange,
    onDialOutChange,
    onShareAudioChange,
    onHostAskToUnmute,
    onCaptionStatusChange,
    onCaptionMessage,
    onCanSeeMyScreen,
    onRecordingISOChange,
    onCaptionDisable,
    onLiveStreamStatusChange,
    onVideoScreenshotTaken,
    onShareViewScreenshotTaken
  ]);
  useUnmount(() => {
    if (zmClient.getSessionInfo().isInMeeting) {
      if (isStartedAudio) {
        mediaStream?.stopAudio();
      }
      if (isStartedVideo) {
        mediaStream?.stopVideo();
      }
      mediaStream?.stopShareScreen();
      pipWindow?.close();
    }
  });
  useMount(() => {
    if (!mediaStream) {
      return;
    }
    setIsSupportPhone(!!mediaStream.isSupportPhoneFeature());
    setPhoneCountryList(mediaStream.getSupportCountryInfo() || []);
    setSharePrivileg(mediaStream.getSharePrivilege());
    if (isAndroidOrIOSBrowser()) {
      setCameraList([
        { deviceId: MobileVideoFacingMode.User, label: 'Front-facing' },
        { deviceId: MobileVideoFacingMode.Environment, label: 'Rear-facing' }
      ]);
    }
  });

  useEffect(() => {
    const isInMeeting = zmClient.getSessionInfo().isInMeeting;
    if (!isInMeeting) {
      return;
    }
    if (deviceSettingCtx.isStartedVideo) {
      onCameraClick();
    }
    if (deviceSettingCtx.isStartedAudio) {
      onMicrophoneClick();
    }
  }, [zmClient.getSessionInfo().isInMeeting]);
  useEffect(() => {
    if (timerState !== 'finished') {
      return;
    }
    onLeaveClick();
  }, [timerState === 'finished']);

  const buttonRef = useRef<ElementRef<'button'>>(null);
  const recordingButtons: RecordButtonProps[] = getRecordingButtons(recordingStatus, zmClient.isHost());
  const onOpenModal = (type: ModalType) => {
    setOpenModal(type);
  };
  const logger = useLogger();
  // const { canvasRef, videoRef, startRecording, stopRecording, isRecording } = useSegmentedZoomRecorder({
  //   stream: mediaStream,
  //   userId: zmClient.getCurrentUserInfo().userId,
  //   segmentDurationSec: 180,
  //   isStartedAudio: isStartedAudio,
  //   isStartedVideo: isStartedVideo,
  //   onSegmentAvailable: async (blob, startedAt) => {
  //     logger.log(blob.size);
  //     const response = await requestLessonApi(() =>
  //       lessonApi.lessonControllerGetUploadUrlOfRecordedFile(lessonCtx?.lessonId, lessonCtx?.roomId, startedAt)
  //     );
  //     if (!Array.isArray(response.data.data)) {
  //       console.error('Upload URL could not be retrieved.');
  //       return;
  //     }
  //     try {
  //       await fetch(response.data.data[0], {
  //         method: 'PUT',
  //         body: blob,
  //         headers: { 'Content-type': blob.type }
  //       });
  //     } catch (error) {
  //       logger.error(error);
  //     }
  //   }
  // });

  if (!zmClient.getSessionInfo().isInMeeting) {
    let Contens = <LoadingOutlined style={{ fontSize: '86px', color: '#fff' }} />;
    if (completeTermination) {
      Contens = <>The session was closed. Please close the screen.</>;
    } else if (lessonClosing) {
      Contens = <>The session was closed. Please close the screen.</>;
    }
    return (
      <Flex
        height={'100svh'}
        width={'100vw'}
        position={'absolute'}
        left={0}
        bottom={0}
        background={'#000'}
        justifyContent={'center'}
        alignItems={'center'}
        zIndex={99}
        color={'#fff'}
      >
        {Contens}
      </Flex>
    );
  }

  return (
    <>
      <AudioRecording
        leaveSession={lessonClosing}
        userIdentity={zmClient?.getCurrentUserInfo()?.userIdentity || ''}
        onAudioTermination={onAudioTermination}
      />
      <VideoRecording
        onRecVideoAdd={(userId: number) => {
          setRecordingVideoUsers((prev) => [...new Set([...prev, userId])]);
        }}
        leaveSession={lessonClosing}
        key={zmClient.getSessionInfo().userId + '-rec-video'}
        isStartedVideo={isStartedVideo}
        userId={zmClient.getSessionInfo().userId}
        userName={zmClient.getSessionInfo().userName}
        onVideoTermination={onVideoTermination}
        userIdentity={zmClient?.getCurrentUserInfo()?.userIdentity || ''}
      />
      {!isMobile() &&
        participants?.map((participant) => (
          <VideoRecording
            onRecVideoAdd={(userId: number) => {
              setRecordingVideoUsers((prev) => [...new Set([...prev, userId])]);
            }}
            leaveSession={lessonClosing}
            key={participant.userId + '-rec-video'}
            isStartedVideo={participant.bVideoOn}
            userId={participant.userId}
            userName={participant.displayName}
            onVideoTermination={onVideoTermination}
            userIdentity={participant.userIdentity || ''}
          />
        ))}

      <Flex style={{ position: 'absolute', zIndex: 100, right: 0, left: 0 }} margin={'auto'} justifyContent={'center'}>
        <RemainingTimeTimer timerState={timerState} timerDuration={timerDuration} />
        <ForceExitTimer timerState={timerState} remainingSecondsToClose={remainingSecondsToClose} />
      </Flex>
      <div className={classNames('video-footer', className)} style={{ background: '#f0f0f0', gap: '10px' }}>
        <Box flexGrow={1}></Box>
        <Flex flexGrow={3} justifyContent={'center'} alignItems={'center'} gap={'10px'}>
          {isAudioEnable && (
            <MicrophoneButton
              isStartedAudio={isStartedAudio}
              isMuted={isMuted}
              audio={audio}
              phoneCountryList={phoneCountryList}
              phoneCallStatus={getPhoneCallStatusDescription(phoneCallStatus)}
              onMicrophoneClick={onMicrophoneClick}
              onMicrophoneMenuClick={onMicrophoneMenuClick}
              microphoneList={micList}
              speakerList={speakerList}
              activeMicrophone={activeMicrophone}
              activeSpeaker={activeSpeaker}
              disabled={isComputerAudioDisabled}
              isMicrophoneForbidden={isMicrophoneForbidden}
            />
          )}
          <CameraButton
            isStartedVideo={isStartedVideo}
            onCameraClick={onCameraClick}
            onSwitchCamera={onSwitchCamera}
            onMirrorVideo={onMirrorVideo}
            onBlurBackground={onBlurBackground}
            onSelectVideoPlayback={onSelectVideoPlayback}
            activePlaybackUrl={activePlaybackUrl}
            cameraList={cameraList}
            activeCamera={activeCamera}
            isMirrored={isMirrored}
            isBlur={isBlur}
            cameraSwitching={cameraSwitching}
          />
          {sharing && !isMobile() && (
            <>
              {/* onScreenShareClickと同じハンドラで直接実行させるとユーザー操作（navigator.userActivation参照）ではないと判断されてしまうため、ダミーを定義 */}
              <button style={{ display: 'none' }} ref={buttonRef} onClick={startPiPView}></button>
              <ScreenShareButton
                sharePrivilege={sharePrivilege}
                isHostOrManager={zmClient.isHost() || zmClient.isManager()}
                onScreenShareClick={() => {
                  onScreenShareClick();
                  if (mediaStream?.getShareStatus() === ShareStatus.End && selfShareCanvas) {
                    buttonRef.current && buttonRef.current.click();
                  }
                  // if (mediaStream?.getShareStatus() === ShareStatus.End && selfShareCanvas) {
                  //   try {
                  //     startPiPView();
                  //   } catch (error) {
                  //     console.error(error);
                  //   }
                  // }
                }}
                onSharePrivilegeClick={async (privilege) => {
                  await mediaStream?.setSharePrivilege(privilege);
                  setSharePrivileg(privilege);
                }}
                isSharing={shareStatus !== ShareStatus.End}
              />
            </>
          )}

          <Button color={'#fff'} colorPalette={'blue'} onClick={togleChatView}>
            <IconFont type="icon-chat" /> {isMobile() || LANG.KT_0100_15}
          </Button>
        </Flex>
        <Box flexGrow={1}>
          <LeaveButton
            onLeaveClick={() => {
              sendLoggerService.sendLog({
                action: '退室ボタン押下',
                lessonId: lessonCtx?.lessonId ?? '',
                roomId: lessonCtx?.roomId ?? '',
                userId: `${zmClient.getSessionInfo().userId}`,
                page: currentPage,
                advancedInfo: {}
              });

              onOpenModal('Leave');
            }}
            isHost={zmClient.isHost()}
            onEndClick={onEndClick}
          />
        </Box>

        {!mediaStream?.isSupportVirtualBackground() && (
          <VideoMaskModel visible={videoMaskVisible} setVisible={setVideoMaskVisible} isMirrored={isMirrored} />
        )}
      </div>
      <LeaveModal
        isOpen={openModal === 'Leave'}
        onCloseModal={() => {
          sendLoggerService.sendLog({
            action: '退室キャンセルボタン押下',
            lessonId: lessonCtx?.lessonId ?? '',
            roomId: lessonCtx?.roomId ?? '',
            userId: `${zmClient.getSessionInfo().userId}`,
            page: currentPage,
            advancedInfo: {}
          });

          setOpenModal('');
        }}
        onLeave={onLeaveClick}
      />
    </>
  );
};
export default VideoFooter;
